import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { libraryError } from '@/lib/libraryAccess';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, Package, PlayCircle, ShieldCheck } from 'lucide-react';
import { LibraryEmptyState, LibraryErrorState, LibraryLoading } from '@/components/forms/LibraryStates';
import { useLibraryBranding } from '@/lib/libraryBranding';

function Report({ rep }) {
  if (!rep) return null;
  return (
    <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm mt-3" data-testid="pack-report">
      <dt className="text-muted-foreground">Action</dt><dd data-testid="pack-report-action">{rep.outcome || rep.action}</dd>
      <dt className="text-muted-foreground">Version</dt><dd>v{rep.pack_version}</dd>
      <dt className="text-muted-foreground">Templates</dt><dd>{rep.templates} ({(rep.library_keys || []).join(', ')})</dd>
      <dt className="text-muted-foreground">Checksum</dt><dd className="font-mono text-xs break-all">{rep.checksum}</dd>
      {'inserted' in rep && <><dt className="text-muted-foreground">Inserted / activated / retired</dt><dd>{rep.inserted} / {rep.activated ?? '—'} / {rep.retired}</dd></>}
      {rep.dry_run && <><dt className="text-muted-foreground">Mode</dt><dd>Dry run — nothing written</dd></>}
    </dl>
  );
}

// Platform-only console. UI gate: forms.library_admin. Backend additionally requires cross-organisation authority (403 LIBRARY_ADMIN_REQUIRED).
export default function FormLibraryPacksPage() {
  const navigate = useNavigate();
  const brand = useLibraryBranding();
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reports, setReports] = useState({});
  const [busy, setBusy] = useState(null);
  const [confirmLoad, setConfirmLoad] = useState(null); // pack_id

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { const { data } = await api.get('/forms/library/packs'); setPacks(data.items || []); }
    catch (e) { setError(libraryError(e)); setPacks([]); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const run = async (packId, mode) => {
    setBusy(`${packId}:${mode}`);
    try {
      const { data } = await api.post(`/forms/library/packs/${packId}/${mode}`);
      setReports(r => ({ ...r, [packId]: { ...data.data, error: null } }));
      toast.success(mode === 'validate' ? 'Pack validated (dry run)' : `Pack ${data.data.outcome === 'COMMITTED' ? 'loaded' : data.data.action || data.data.outcome}`);
      if (mode === 'load') load();
    } catch (e) { setReports(r => ({ ...r, [packId]: { error: libraryError(e) } })); }
    setBusy(null); setConfirmLoad(null);
  };

  return (
    <div className="space-y-6" data-testid="form-library-packs-page">
      <div className="flex items-start gap-3">
        <Button variant="ghost" size="icon" aria-label="Back to Template Library" onClick={() => navigate('/forms/library')} data-testid="packs-back-btn"><ArrowLeft className="w-5 h-5" /></Button>
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><Package className="w-6 h-6" />Library Packs</h1>
          <p className="text-muted-foreground mt-1 text-sm">Platform administration · bundled packs are validated and loaded explicitly; the committed pack head is the only source of what is effective.</p>
        </div>
      </div>

      {loading ? <LibraryLoading label="Loading packs…" /> : error ? <LibraryErrorState error={error} onRetry={load} /> : packs.length === 0 ? (
        <LibraryEmptyState title="No bundled packs" hint="The bundled pack directory contains no allow-listed packs." />
      ) : (
        <div className="space-y-3" data-testid="packs-list">
          {packs.map(p => {
            const rep = reports[p.pack_id];
            return (
              <Card key={p.pack_id} data-testid={`pack-row-${p.pack_id}`}>
                <CardHeader className="pb-2 flex-row items-center justify-between gap-3 flex-wrap">
                  <div>
                    <CardTitle className="text-base" data-testid={`pack-title-${p.pack_id}`}>{brand.libraryName}</CardTitle>
                    <p className="text-xs text-muted-foreground mt-0.5">Publisher: <span data-testid={`pack-publisher-${p.pack_id}`}>{brand.publisher}</span> · Pack ID: <span className="font-mono" data-testid={`pack-machine-id-${p.pack_id}`}>{p.pack_id}</span></p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" disabled={!!busy} onClick={() => run(p.pack_id, 'validate')} data-testid={`pack-validate-${p.pack_id}`}><ShieldCheck className="w-4 h-4 mr-1.5" />{busy === `${p.pack_id}:validate` ? 'Validating…' : 'Validate (dry run)'}</Button>
                    <Button size="sm" disabled={!!busy} onClick={() => setConfirmLoad(p.pack_id)} data-testid={`pack-load-${p.pack_id}`}><PlayCircle className="w-4 h-4 mr-1.5" />Load…</Button>
                  </div>
                </CardHeader>
                <CardContent className="text-sm space-y-1">
                  <p>Committed head: {p.head ? <Badge data-testid={`pack-head-${p.pack_id}`}>v{p.head.active_pack_version} · rev {p.head.revision}</Badge> : <Badge variant="outline" data-testid={`pack-head-${p.pack_id}`}>none</Badge>}</p>
                  <p className="text-muted-foreground text-xs">Stored versions: {(p.versions || []).length ? p.versions.join(', ') : 'none'}{p.head?.activated_at ? ` · activated ${p.head.activated_at}` : ''}</p>
                  {rep?.error && <div role="alert" className="rounded-md border border-red-200 bg-red-50 p-3 text-red-800 mt-2" data-testid={`pack-error-${p.pack_id}`}><p className="font-medium">{rep.error.message}</p>{rep.error.code && <p className="text-xs font-mono">{rep.error.code}</p>}</div>}
                  {rep && !rep.error && <Report rep={rep} />}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={!!confirmLoad} onOpenChange={v => { if (!v) setConfirmLoad(null); }}>
        <DialogContent className="max-w-md" data-testid="pack-load-dialog">
          <DialogHeader>
            <DialogTitle>Load {brand.libraryName} pack (<span className="font-mono">{confirmLoad}</span>)?</DialogTitle>
            <DialogDescription>Loading stores the pack's templates as immutable library masters and switches the committed head to this version in one step. Organisations' existing imported copies are never changed. Same version with different content is rejected; older versions are rejected.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmLoad(null)} disabled={!!busy} data-testid="pack-load-cancel">Cancel</Button>
            <Button onClick={() => run(confirmLoad, 'load')} disabled={!!busy} data-testid="pack-load-confirm">{busy ? 'Loading…' : 'Load pack'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
